// React
import { useEffect, useState } from "react";
import { Link as ReactRouterLink, useNavigate, useParams } from "react-router-dom";
import React from "react";

// Hooks and Redux
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { refreshMasterData } from "../../../redux/slices/masterDataSlice"

// MUI Components
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';

// MUI Icons
import HomeIcon from '@mui/icons-material/Home';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';

// Utils
import { ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import jwtDecode from "jwt-decode";
import Swal from "sweetalert2";

// Styles
import { green } from "@mui/material/colors";

// Types
import { TokenPayload } from "../../../@types";

// Services
import {
    createWorkGroup,
    updateWorkGroup,
    getWorkGroupById,
} from "../../../services/MasterDataService";

interface FormData {
    external_code: string;
    label: string;
    is_active: boolean;
}

type ModeType = "add" | "edit"

interface MyEditorProps {
  type: ModeType
  // editId?: string
}

const ManageWorkGroup: React.FC<MyEditorProps> = ({ type = "add" }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams();
  const isEdit = Boolean(id)

  const dispatch = useAppDispatch()

  const userRoleType = useAppSelector((state) => state.user.roletype)
  const havePermission = userRoleType === "Super Admin"

  const [formData, setFormData] = useState<FormData>({
    external_code: "",
    label: "",
    is_active: true,
  })
  const [dataLoading, setDataLoading] = useState<boolean>(isEdit)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  if (!havePermission) {
    navigate("/forbidden", { replace: true })
  }

  useEffect(() => {
    if (!isEdit || !id) return;
    const fetchData = async () => {
      try {
        setDataLoading(true)
        if (id) {
          const response = await getWorkGroupById(id)
          if (response.data?.success === false) {
            Swal.fire({
              title:
                i18n.language === "th"
                  ? response.data?.message_th
                  : response.data?.message_en,
              icon: "error",
              confirmButtonColor: "#43A047",
              confirmButtonText: "OK!",
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then((result) => {
              if (result.isConfirmed)
                navigate("/admin/masterdata/work-groups", { replace: true })
            })
            return
          }
          const item = response.data?.workGroup
          setFormData({
            external_code: item.external_code,
            label: item.label,
            is_active: item.is_active,
          })
        }
      } catch (error) {
        navigate("/servererror", { replace: true })
      } finally {
        setDataLoading(false)
      }
    }
    // fetchData();
    if (type === "edit" && typeof id === "string" && id.length > 0) {
      fetchData()
    }
  }, [id, navigate, i18n.language])

  const formValidationSchema = Yup.object().shape({
    ...(type === "edit"
      ? {}
      : {
          external_code: Yup.string().required(
            `${t("External code is required")}`
          ),
        }),
    label: Yup.string().required(`${t("Label is required")}`),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(formValidationSchema) })

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, is_active: event.target.checked }))
  }

  const onSubmit = (values: any) => {
    const submit = async () => {
      try {
        const parsedToken: TokenPayload = jwtDecode(localStorage.access_token)
        setSubmitLoading(true)
        let response
        if (type === "edit") {
          response = await updateWorkGroup(id as string, {
            external_code: formData.external_code,
            label: values.label,
            is_active: formData.is_active,
            modified_by: parsedToken.Username,
          })
        } else {
          response = await createWorkGroup({
            external_code: values.external_code,
            label: values.label,
            is_active: formData.is_active,
            created_by: parsedToken.Username,
          })
        }
        if (response) {
          setSubmitLoading(false)
          const responseData = response.data
          if (!responseData?.success) {
            toast.error(
              i18n.language === "th"
                ? responseData.message_th
                : responseData.message_en,
              { position: "top-center", autoClose: 3000, theme: "light" }
            )
          } else {
            Swal.fire({
              title:
                i18n.language === "th"
                  ? responseData.message_th
                  : responseData.message_en,
              icon: "success",
              confirmButtonColor: "#43A047",
              confirmButtonText: "OK!",
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then((result) => {
              if (result.isConfirmed) {
                dispatch(refreshMasterData())
                navigate("/admin/masterdata/work-groups", { replace: true })
              }
            })
          }
        }
      } catch (error) {
        navigate("/servererror", { replace: true })
      }
    }
    submit()
  }

  if (dataLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <ToastContainer style={{ width: "400px" }} />
      {havePermission && (
        <>
          <Box sx={{ height: "auto", maxWidth: "100%", mt: 2 }}>
            <Box display="flex" gap={3} sx={{ mb: 3 }}>
              <Box
                display="flex"
                component={ReactRouterLink}
                onClick={() => window.history.back()}
                sx={{
                  textDecoration: "none",
                  color: "#0d47a1",
                  alignItems: "center",
                  ":hover": { color: "#1976d2" },
                }}
              >
                <ChevronLeftIcon fontSize="small" />
                <Typography sx={{ fontWeight: "bold" }}>{t("Back")}</Typography>
              </Box>
              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  color="inherit"
                  component={ReactRouterLink}
                  to="/admin"
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {t("Dashboard")}
                </Link>
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  color="inherit"
                  component={ReactRouterLink}
                  to="/admin/masterdata/work-groups"
                >
                  <GroupsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {t("Work Group")}
                </Link>
                <Typography
                  sx={{ display: "flex", alignItems: "center" }}
                  color="text.primary"
                >
                  {isEdit ? (
                    <>
                      <EditIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                      {t("Edit Work Group")}
                    </>
                  ) : (
                    <>
                      <AddCircleOutlineIcon
                        sx={{ mr: 0.5 }}
                        fontSize="inherit"
                      />
                      {t("Create New Work Group")}
                    </>
                  )}
                </Typography>
              </Breadcrumbs>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "70px",
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", textAlign: "left" }}
            >
              {isEdit ? t("Edit Work Group") : t("Create New Work Group")}
            </Typography>
          </Box>

          <Box
            component="form"
            noValidate
            sx={{ mt: 3 }}
            onKeyDown={(e: React.KeyboardEvent) =>
              e.key === "Enter" && e.preventDefault()
            }
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="externalCode"
                  label={t("External Code")}
                  disabled={isEdit}
                  {...(!isEdit ? register("external_code") : {})}
                  error={!!errors.external_code}
                  value={isEdit ? formData.external_code : undefined}
                  onChange={
                    isEdit
                      ? undefined
                      : (event: React.ChangeEvent<HTMLInputElement>) => {
                          setFormData((prev) => ({
                            ...prev,
                            external_code: event.target.value,
                          }))
                          ;(errors as any).external_code = undefined
                        }
                  }
                />
                <Typography
                  variant="inherit"
                  color="error"
                  align="left"
                  pt={1}
                  fontSize={13}
                >
                  {errors.external_code?.message}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="label"
                  label={t("Label")}
                  {...register("label")}
                  error={!!errors.label}
                  value={formData.label}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData((prev) => ({
                      ...prev,
                      label: event.target.value,
                    }))
                    ;(errors as any).label = undefined
                  }}
                />
                <Typography
                  variant="inherit"
                  color="error"
                  align="left"
                  pt={1}
                  fontSize={13}
                >
                  {errors.label?.message}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  label={t("Active")}
                  control={
                    <Checkbox
                      checked={formData.is_active}
                      onChange={handleActiveChange}
                      inputProps={{ "aria-label": "Active" }}
                    />
                  }
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  sx={{ mt: 5, mb: 2, color: "white" }}
                  onClick={handleSubmit(onSubmit)}
                  disabled={submitLoading}
                >
                  {isEdit ? t("Save") : t("Create")}
                  {submitLoading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: green[500],
                        position: "absolute",
                        zIndex: 999,
                      }}
                    />
                  )}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 5, mb: 2 }}
                  component={ReactRouterLink}
                  to="/admin/masterdata/work-groups"
                >
                  {t("Cancel")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </>
  )
};

export default ManageWorkGroup;
